// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { createFromProduct, ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Notify from "@pace/App/Components/Notify";
import ProductPriceAndActions, {
    ProductPriceAndActionsStyle,
} from "@pace/App/Components/Product/ProductPriceAndActions";
import updatePreviouslyPurchasedSortPaging from "@pace/App/Handlers/xPreviouslyPurchased/UpdatePreviouslyPurchasedSortAndPaging";
import updateQuantity from "@pace/App/Handlers/xPreviouslyPurchased/UpdateQuantity";
import { PreviouslyPurchasedPageDataContext } from "@pace/App/Pages/PreviouslyPurchasedPage";
import { CustomAppState } from "@pace/App/Store/Reducers";
import { getPreviouslyPurchasedDataView } from "@pace/App/Store/Selectors/PreviouslyPurchasedSelectors";
import { xHasRoles } from "@pace/App/Store/Selectors/SessionSelectors";
import ProductListProductImage, {
    ProductListProductImageStyles,
} from "@pace/Overrides/Widgets/ProductList/ProductListProductImage";
import ProductListProductInformation, {
    ProductListProductInformationStyles,
} from "@pace/Overrides/Widgets/ProductList/ProductListProductInformation";
import { customXngageWidget, sharedDateTimeOptions } from "@pace/XDL/constants";
import React, { useContext, useMemo } from "react";
import { connect, ResolveThunks } from "react-redux";

const mapStateToProps = (state: CustomAppState) => {
    const filters = state.xPreviouslyPurchasedFilterList.previouslyPurchasedFilters;
    const previouslyPurchasedDataView = getPreviouslyPurchasedDataView(state, filters);

    return {
        previouslyPurchasedParameter: filters,
        previouslyPurchasedDataView,
        language: state.context.session.language,
        showPricing: state.context.showPricing,
        orderHistoryLink: getPageLinkByPageType(state, "OrderHistoryPage")?.url,
        cannotViewOrders: xHasRoles(state, ["CannotViewOrders"]),
    };
};

const mapDispatchToProps = {
    updatePreviouslyPurchasedSortPaging,
    updateQuantity,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface PreviouslyPurchasedStyles {
    container?: InjectableCss;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    noResultsContainer?: InjectableCss;
    noResultsText?: TypographyPresentationProps;
    dataTable?: DataTableProps;
    dataTableHead?: DataTableHeadProps;
    dataTableBody?: DataTableBodyProps;
    dataTableRow?: DataTableRowProps;
    orderDateHeader?: DataTableHeaderProps;
    productNameHeader?: DataTableHeaderProps;
    qtyShippedHeader?: DataTableHeaderProps;
    priceHeader?: DataTableHeaderProps;
    orderDateCell?: DataTableCellBaseProps;
    productName?: DataTableCellProps;
    productWrapper?: InjectableCss;
    qtyShipped?: DataTableCellProps;
    price?: DataTableCellProps;
    productInfo?: ProductListProductInformationStyles;
    productImage?: ProductListProductImageStyles;
    productActions?: ProductPriceAndActionsStyle;
    productActionsWithPrice?: ProductPriceAndActionsStyle;
}

export const previouslyPurchasedStyles: PreviouslyPurchasedStyles = {};

const styles = previouslyPurchasedStyles;

const PreviouslyPurchasedTable = ({
    previouslyPurchasedParameter,
    previouslyPurchasedDataView,
    language,
    orderHistoryLink,
    updatePreviouslyPurchasedSortPaging,
    cannotViewOrders,
    updateQuantity,
}: Props) => {
    const orderDateSort = previouslyPurchasedParameter.sortBy?.orderDateSortType === "asc" ? "ascending" : "descending";
    const toggleSortByDate = () => {
        updatePreviouslyPurchasedSortPaging({
            sortBy: {
                orderDateSortType: orderDateSort === "ascending" ? "desc" : "asc",
            },
        });
    };

    const { products, productInfoList } = useContext(PreviouslyPurchasedPageDataContext);

    const productByErpNumber = useMemo(
        () =>
            products.reduce(
                (acc: { [key: string]: ProductModel }, product) => ({ ...acc, [product.productNumber]: product }),
                {},
            ),
        [products],
    );

    const rows = previouslyPurchasedDataView.value?.map(productLine => {
        const productData = productByErpNumber[productLine.productErpNumber];
        return {
            ...productLine,
            orderDate: productLine.orderDate
                ? getLocalizedDateTime({
                      dateTime: new Date(productLine.orderDate),
                      language,
                      // Fix OOTB Date Format
                      options: {
                          ...sharedDateTimeOptions,
                      },
                  })
                : "",
            productData,
            productInfo: productData
                ? productInfoList[productData.id] || createFromProduct(productData)
                : ({} as ProductInfo),
            onQtyOrderedChanged: (o: number) => {
                updateQuantity({
                    id: previouslyPurchasedParameter,
                    qtyOrdered: o,
                    productId: productData.id,
                    unitOfMeasure: productLine.unitOfMeasure,
                });
            },
            onUnitOfMeasureChanged: (o: string) => {
                updateQuantity({
                    id: previouslyPurchasedParameter,
                    qtyOrdered: productLine.qtyOrdered,
                    productId: productData.id,
                    unitOfMeasure: o,
                });
            },
        };
    });
    return (
        <StyledWrapper {...styles.container}>
            <DataTable {...styles.dataTable}>
                <DataTableHead {...styles.dataTableHead}>
                    <DataTableHeader {...styles.productNameHeader}>{translate("Product Name")}</DataTableHeader>
                    <DataTableHeader tight {...styles.qtyShippedHeader}>
                        {translate("Qty Shipped")}
                    </DataTableHeader>
                    <DataTableHeader
                        tight
                        sorted={orderDateSort}
                        onSortClick={toggleSortByDate}
                        {...styles.orderDateHeader}
                    >
                        {translate("Last Ordered")}
                    </DataTableHeader>
                    <DataTableHeader {...styles.priceHeader}>{translate("Price")}</DataTableHeader>
                </DataTableHead>
                <DataTableBody {...styles.dataTableBody}>
                    {!previouslyPurchasedDataView.value && (
                        <DataTableRow {...styles.noResultsContainer}>
                            <DataTableCell colSpan={4} {...styles.centeringWrapper}>
                                <LoadingSpinner {...styles.spinner}></LoadingSpinner>
                            </DataTableCell>
                        </DataTableRow>
                    )}
                    {previouslyPurchasedDataView.value?.length === 0 && (
                        <DataTableRow {...styles.noResultsContainer}>
                            <DataTableCell colSpan={4} aria-colspan={4} {...styles.centeringWrapper}>
                                <Notify type="info" fullWidth showClose={false} {...styles.noResultsText}>
                                    {siteMessage("Custom_No_Previously_Purchased_Found")}
                                </Notify>
                            </DataTableCell>
                        </DataTableRow>
                    )}
                    {rows?.map(
                        ({
                            id,
                            productData,
                            productInfo,
                            qtyShipped,
                            orderDate,
                            productErpNumber,
                            onQtyOrderedChanged,
                        }) =>
                            productData ? (
                                <DataTableRow
                                    data-test-selector="previouslyPurchased_Row"
                                    data-test-key={id}
                                    key={id}
                                    {...styles.dataTableRow}
                                >
                                    <ProductContext.Provider
                                        value={{ product: productData, productInfo, onQtyOrderedChanged }}
                                    >
                                        <DataTableCell
                                            {...styles.productName}
                                            data-test-selector="previouslyPurchased_ProductNumber"
                                        >
                                            <StyledWrapper {...styles.productWrapper}>
                                                <ProductListProductImage
                                                    showImage
                                                    showCompare={false}
                                                    showTextBadge
                                                    extendedStyles={styles.productImage}
                                                />
                                                <ProductListProductInformation
                                                    showTitle
                                                    showBrand
                                                    showPartNumbers
                                                    showAvailability={false}
                                                    showAttributes={false}
                                                    extendedStyles={styles.productInfo}
                                                />
                                            </StyledWrapper>
                                        </DataTableCell>
                                        <DataTableCell {...styles.qtyShipped}>{qtyShipped}</DataTableCell>
                                        <DataTableCell {...styles.orderDateCell}>{orderDate}</DataTableCell>
                                        <DataTableCell {...styles.price}>
                                            <ProductPriceAndActions
                                                showPrice
                                                showAddToCartActions
                                                showInfo
                                                showAvailability
                                                showAddToList
                                                showImageBadges
                                                {...(cannotViewOrders
                                                    ? {}
                                                    : {
                                                          goToPageLink: `${orderHistoryLink}?productErpNumber=${productErpNumber}&customerSequence=-1`,
                                                          goToPageLabel: translate("View Orders"),
                                                      })}
                                                extendedStyles={styles.productActionsWithPrice}
                                            />
                                        </DataTableCell>
                                    </ProductContext.Provider>
                                </DataTableRow>
                            ) : null,
                    )}
                </DataTableBody>
            </DataTable>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(PreviouslyPurchasedTable),
    definition: {
        group: customXngageWidget as any,
        displayName: "Previously Purchased Table",
        allowedContexts: ["PreviouslyPurchasedPage"],
        isSystem: true,
    },
};

export default widgetModule;
